import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Fade,
  Link as MuiLink,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircleOutline } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext';
import authService from '../../services/auth.service';
import AuthLayout from '../../components/layout/AuthLayout';
import PasswordStrengthBar from '../../components/common/PasswordStrengthBar';
import OtpInput from '../../components/common/OtpInput';
import axios from 'axios';

type Step = 'email' | 'verify' | 'reset' | 'success';

const cardSx = {
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
  bgcolor: 'rgba(20, 20, 30, 0.58)',
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
};

const inputFieldSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
  },
};

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [step, setStep] = useState<Step>('email');

  // Email step
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  // OTP step
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [resetToken, setResetToken] = useState('');

  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Reset step
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // General state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Success auto-redirect
  const [redirectCountdown, setRedirectCountdown] = useState(5);

  // Email validation
  const isEmailValid = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  const emailError = emailTouched && email && !isEmailValid(email);

  // Password validation
  const isPasswordValid = (val: string) => {
    return val.length >= 8 && /[A-Z]/.test(val) && /[a-z]/.test(val) && /\d/.test(val);
  };
  const passwordInvalid = touched.password && !isPasswordValid(password);
  const confirmPasswordError = touched.confirmPassword && confirmPassword !== password;
  const resetFormValid = isPasswordValid(password) && confirmPassword === password;

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [resendCooldown]);

  // Success auto-redirect
  useEffect(() => {
    if (step !== 'success') return;
    const timer = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/login', { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [step, navigate]);

  // Auto-submit OTP on 6 digits
  useEffect(() => {
    if (otpValue.length === 6 && step === 'verify' && !loading) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpValue]);

  // Step 1: Send code
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEmailValid(email)) return;

    setLoading(true);
    setError('');

    try {
      await authService.forgotPassword(email);
    } catch {
      // Always proceed — backend never reveals if email exists
    } finally {
      setLoading(false);
      setStep('verify');
      setResendCooldown(60);
    }
  };

  // Step 2: Verify code
  const handleVerify = async () => {
    if (otpValue.length < 6) return;

    setLoading(true);
    setVerifyError('');
    setOtpError(false);

    try {
      const res = await authService.verifyResetCode({ email, code: otpValue });
      setResetToken(res.data.token);
      setStep('reset');
    } catch (err) {
      setOtpError(true);
      if (axios.isAxiosError(err)) {
        const message = err.response?.data?.message;
        setVerifyError(message || 'Invalid code. Please try again.');
      } else {
        setVerifyError('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // OTP change handler
  const handleOtpChange = (val: string) => {
    setOtpValue(val);
    setOtpError(false);
    setVerifyError('');
  };

  // Resend code
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await authService.forgotPassword(email);
      setResendCooldown(60);
      setOtpValue('');
      setOtpError(false);
      setVerifyError('');
      showNotification('New code sent', 'success');
    } catch {
      showNotification('Failed to resend code', 'error');
    }
  };

  // Step 3: Reset password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetFormValid) return;

    setLoading(true);
    setError('');

    try {
      await authService.resetPassword({ email, token: resetToken, password });
      setStep('success');
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const message = err.response?.data?.message;
        if (err.response?.status === 400 && message?.toLowerCase().includes('expired')) {
          setError('Reset link expired. Please start over.');
        } else {
          setError(message || 'Password reset failed. Please try again.');
        }
      } else {
        setError('Password reset failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Back handlers
  const handleBackToEmail = () => {
    setStep('email');
    setOtpValue('');
    setOtpError(false);
    setVerifyError('');
  };

  const handleBackToStart = () => {
    setStep('email');
    setOtpValue('');
    setOtpError(false);
    setVerifyError('');
    setResetToken('');
    setPassword('');
    setConfirmPassword('');
    setTouched({});
    setError('');
  };

  // === STEP 4: SUCCESS ===
  if (step === 'success') {
    return (
      <AuthLayout maxWidth="480px">
        <Fade in>
          <Card sx={cardSx}>
            <CardContent sx={{ p: '48px', textAlign: 'center' }}>
              <CheckCircleOutline sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
              <Typography variant="h5" fontWeight={600} color="#fff" gutterBottom>
                Password reset successfully!
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
                You can now sign in with your new password.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                component={Link}
                to="/login"
                sx={{ height: 48, fontSize: 16, fontWeight: 500, borderRadius: '8px' }}
              >
                Go to Sign In
              </Button>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 2, display: 'block' }}>
                Redirecting in {redirectCountdown}s...
              </Typography>
            </CardContent>
          </Card>
        </Fade>
      </AuthLayout>
    );
  }

  // === STEP 3: RESET PASSWORD ===
  if (step === 'reset') {
    return (
      <AuthLayout maxWidth="480px">
        <Fade in>
          <Card sx={cardSx}>
            <CardContent sx={{ p: '48px' }}>
              <Typography variant="h5" fontWeight={600} color="#fff" gutterBottom>
                Set new password
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
                Enter your new password below.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                  {error.includes('start over') && (
                    <>
                      {' '}
                      <MuiLink
                        component="button"
                        onClick={handleBackToStart}
                        sx={{ color: 'inherit', fontWeight: 600, cursor: 'pointer' }}
                      >
                        Start over
                      </MuiLink>
                    </>
                  )}
                </Alert>
              )}

              <Box component="form" onSubmit={handleResetPassword}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
                  error={!!passwordInvalid}
                  helperText={passwordInvalid ? 'Min 8 chars, 1 uppercase, 1 lowercase, 1 digit' : ''}
                  disabled={loading}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    sx: { height: 48, fontSize: 16, borderRadius: '8px', color: '#fff' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{ color: 'rgba(255,255,255,0.5)' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                  sx={inputFieldSx}
                />
                <PasswordStrengthBar password={password} />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, confirmPassword: true }))}
                  error={!!confirmPasswordError}
                  helperText={confirmPasswordError ? 'Passwords do not match' : ''}
                  disabled={loading}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    sx: { height: 48, fontSize: 16, borderRadius: '8px', color: '#fff' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                          sx={{ color: 'rgba(255,255,255,0.5)' }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                  sx={inputFieldSx}
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || !resetFormValid}
                  sx={{ mt: 3, height: 48, fontSize: 16, fontWeight: 500, borderRadius: '8px' }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
                </Button>
              </Box>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleBackToStart}
                  sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
                >
                  &larr; Start over
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </AuthLayout>
    );
  }

  // === STEP 2: VERIFY CODE ===
  if (step === 'verify') {
    return (
      <AuthLayout maxWidth="480px">
        <Fade in>
          <Card sx={cardSx}>
            <CardContent sx={{ p: '48px' }}>
              <Typography variant="h5" fontWeight={600} color="#fff" gutterBottom>
                Enter verification code
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
                If an account with that email exists, we've sent a verification code. Check your inbox.
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
                Code sent to <strong style={{ color: '#fff' }}>{email}</strong>
              </Typography>

              {verifyError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {verifyError}
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                <OtpInput
                  value={otpValue}
                  onChange={handleOtpChange}
                  error={otpError}
                  disabled={loading}
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleVerify}
                disabled={loading || otpValue.length < 6}
                sx={{ height: 48, fontSize: 16, fontWeight: 500, borderRadius: '8px' }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </Button>
              </Box>

              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleBackToEmail}
                  sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
                >
                  &larr; Back
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </AuthLayout>
    );
  }

  // === STEP 1: EMAIL ENTRY ===
  return (
    <AuthLayout maxWidth="480px">
      <Fade in>
        <Card sx={cardSx}>
          <CardContent sx={{ p: '48px' }}>
            <Typography variant="h5" fontWeight={600} color="#fff" gutterBottom>
              Reset your password
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
              Enter your email address and we'll send you a verification code.
            </Typography>

            <Box component="form" onSubmit={handleSendCode}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailTouched(false); }}
                onBlur={() => setEmailTouched(true)}
                error={!!emailError}
                helperText={emailError ? 'Please enter a valid email address' : ''}
                disabled={loading}
                margin="normal"
                variant="outlined"
                InputProps={{ sx: { height: 48, fontSize: 16, borderRadius: '8px', color: '#fff' } }}
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                sx={inputFieldSx}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || !isEmailValid(email)}
                sx={{ mt: 3, height: 48, fontSize: 16, fontWeight: 500, borderRadius: '8px' }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Code'}
              </Button>
            </Box>

            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', mt: 3 }}>
              <MuiLink
                component={Link}
                to="/login"
                sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'underline', '&:hover': { color: '#fff' } }}
              >
                &larr; Back to Sign In
              </MuiLink>
            </Typography>
          </CardContent>
        </Card>
      </Fade>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
